import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { PAGE_LABELS } from 'routes'

/**
 * Builds the `{ backTo, backLabel }` router state that a reference link hands to its
 * destination, so the destination's `Header` can render "← Back to <label>"
 * (see `ReferrerBackLink`). Spread the result into a `<Link state={...}>`.
 *
 * Why a hook and not a per-call-site prop: the pattern already existed as an opt-in
 * `backLabel` prop, and 35 of 37 shared navigations simply never passed it — a drill-in
 * from almost any table landed on a page with no way back. Deriving both halves here
 * makes back-navigation the DEFAULT for every reference link instead of something each
 * new call site has to remember. See working-conventions §42.
 *
 * `backTo` is the current location (pathname + query), so returning restores the list's
 * filters/sort/page rather than dumping the user on a reset table.
 */

// A path segment that identifies a record rather than naming a page — a UUID, a bare
// numeric id, or a long opaque slug. Skipped when deriving the label so a detail page
// doesn't produce "Back to 7f3a9c2e-...".
const looksLikeId = (segment: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(segment) || /^\d+$/.test(segment) || segment.length > 24

// Route-grouping prefixes that have no index page of their own, so they never name a
// destination the user can go "back" to.
const GROUPING_SEGMENTS = new Set(['internal', 'settings', 'company', 'personal'])

const titleCase = (segment: string): string =>
  segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

/**
 * @param labelOverride Explicit label for cases the path can't describe — chiefly a
 * detail→detail link, where the derived label would be the parent LIST ("Certificates")
 * while `backTo` actually points at a single record. List → detail (the common table
 * drill-in) derives correctly and needs no override.
 */
export const useReferrerState = (
  labelOverride?: string,
): { backTo: string; backLabel: string } | undefined => {
  const location = useLocation()

  // Memoised so the identity is stable across renders: call sites put `referrer` in
  // useMemo/useCallback dependency arrays (notably DataGrid column factories), and a
  // fresh object each render would rebuild those on every render.
  return useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    // Walk from the end: the nearest segment that names a page (not a record id, not a
    // grouping prefix) is what the user would recognise as "where I just was".
    const named = [...segments].reverse().find(s => !looksLikeId(s) && !GROUPING_SEGMENTS.has(s))

    const backLabel =
      labelOverride ?? PAGE_LABELS[location.pathname.replace(/\/+$/, '')] ?? (named ? titleCase(named) : undefined)
    if (!backLabel) return undefined

    return { backTo: `${location.pathname}${location.search}`, backLabel }
  }, [location.pathname, location.search, labelOverride])
}
